import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';

const dates = value => value ? new Date(value).toLocaleString('id-ID') : '—';
const labels = { unused: 'Belum dipakai', claimed: 'Aktivasi diproses', used: 'Sudah dipakai', revoked: 'Dicabut' };
const durationLabel = token => token.trial || token.duration_hours === 2 ? 'Trial 2 jam' : `${token.days} hari`;
const PAGE_SIZE = 10;

export default function SubscriptionTokens() {
  const [plan, setPlan] = useState('pro');
  const [duration, setDuration] = useState('30');
  const [customDays, setCustomDays] = useState(60);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (requestedPage = page) => {
    try {
      const { data } = await api.get('/admin/subscription-tokens', { params: { page: requestedPage, limit: PAGE_SIZE } });
      setItems(data.items);
      setPage(data.page);
      setPagination({ total: data.total, pages: data.pages });
      setError('');
    }
    catch { setError('Riwayat token belum bisa dimuat.'); }
  }, [page]);

  useEffect(() => { load(); const timer = setInterval(load, 15000); return () => clearInterval(timer); }, [load]);

  const generate = async () => {
    const trial = duration === 'trial';
    const days = duration === 'custom' ? Number(customDays) : Number(duration);
    if (!trial && (!Number.isInteger(days) || days < 1 || days > 3650)) {
      toast.error('Durasi khusus harus 1 sampai 3650 hari');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/subscription-tokens', { plan, days: trial ? 1 : days, trial });
      setIssued(data);
      toast.success(`Token ${plan === 'elite' ? 'Elite' : 'Pro'} berhasil dibuat`);
      await load(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gagal membuat token'); }
    finally { setBusy(false); }
  };

  const revoke = async id => {
    if (!window.confirm('Cabut token ini? Pelanggan tidak akan bisa menukarkannya.')) return;
    setBusy(true);
    try { await api.delete(`/admin/subscription-tokens/${id}`); if (issued?.id === id) setIssued(null); await load(page); }
    catch (e) { toast.error(e.response?.data?.detail || 'Gagal mencabut token'); }
    finally { setBusy(false); }
  };

  return <section className="rounded-xl border border-cyan-400/20 bg-[#0F0F16] p-5 sm:p-6 space-y-5" aria-labelledby="tokens-heading">
    <div>
      <p className="text-xs uppercase tracking-widest text-cyan-400">Langganan pelanggan</p>
      <h2 id="tokens-heading" className="text-xl font-bold mt-1">Generate Token</h2>
      <p className="text-sm text-slate-400 mt-2">Pro untuk 1 akun Telegram, Elite maksimal 3 akun. Masa aktif berjalan sejak token ditukar.</p>
    </div>

    <div className="space-y-2">
      <span className="text-xs uppercase tracking-wider text-slate-500">Paket</span>
      <div className="inline-flex rounded-md border border-white/15 p-1" role="group" aria-label="Paket token">
        {['pro','elite'].map(value => <button key={value} type="button" aria-pressed={plan===value} onClick={() => setPlan(value)} className={`px-5 py-2 rounded text-sm font-semibold ${plan===value?'bg-cyan-400 text-black':'text-slate-300 hover:bg-white/5'}`}>{value === 'elite' ? 'Elite · 3 akun' : 'Pro · 1 akun'}</button>)}
      </div>
    </div>

    <div className="space-y-2">
      <span className="text-xs uppercase tracking-wider text-slate-500">Durasi</span>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Durasi token">
        {[['trial','Trial 2 jam'],['3','3 hari'],['7','7 hari'],['30','30 hari'],['custom','Bebas']].map(([value,label]) => <button key={value} type="button" aria-pressed={duration===value} onClick={() => setDuration(value)} className={`px-4 py-2.5 rounded-md border text-sm font-semibold ${duration===value?'bg-cyan-400/10 border-cyan-400 text-cyan-300':'border-white/15 text-slate-300 hover:border-white/40'}`}>{label}</button>)}
        {duration === 'custom' && <div className="flex items-center gap-2"><Input type="number" min="1" max="3650" value={customDays} onChange={e=>setCustomDays(e.target.value)} aria-label="Durasi khusus dalam hari" className="w-28 h-10 bg-black/30 border-white/15"/><span className="text-sm text-slate-400">hari</span></div>}
      </div>
      {duration === 'trial' && <p className="text-xs text-amber-300">Trial berakhir tepat 2 jam setelah token ditukar dan waktunya terus berjalan.</p>}
    </div>

    <Button disabled={busy} onClick={generate} className="bg-cyan-400 hover:bg-cyan-300 text-black font-bold h-11">{busy ? 'Memproses…' : 'Generate token'}</Button>

    {issued && <div className="rounded-lg border border-cyan-400/30 bg-cyan-400/5 p-4 space-y-3" aria-live="polite">
      <p className="text-sm font-semibold">{issued.plan === 'elite' ? 'Elite' : 'Pro'} · {durationLabel(issued)} — salin dan kirim kepada pelanggan</p>
      <div className="flex flex-col sm:flex-row gap-3"><input aria-label="Token baru" readOnly value={issued.token} onFocus={e=>e.target.select()} className="min-w-0 flex-1 rounded-md bg-black/40 p-3 font-mono text-sm text-cyan-200 border border-white/10"/><Button variant="outline" onClick={async()=>{try{await navigator.clipboard.writeText(issued.token);toast.success('Token disalin');}catch{toast.error('Pilih teks token lalu salin manual');}}}>Salin token</Button></div>
      <p className="text-xs text-slate-400">Kode lengkap hanya ditampilkan setelah dibuat. Simpan sebelum meninggalkan halaman atau membuat token lain.</p>
    </div>}

    {error && <p role="alert" className="text-amber-300 text-sm">{error} <button onClick={()=>load()} className="underline">Coba lagi</button></p>}
    <div className="overflow-x-auto"><table className="w-full text-left text-sm"><caption className="text-left text-slate-400 text-xs pb-3">Riwayat token · 10 per halaman</caption><thead className="text-slate-400 border-b border-white/10"><tr>{['Token','Paket','Durasi','Status','Dibuat','Aksi'].map(h=><th key={h} className="py-3 pr-4 font-medium">{h}</th>)}</tr></thead><tbody>
      {items.map(t=><tr key={t.id} className="border-b border-white/5"><td className="py-3 pr-4 font-mono text-xs">{t.label}</td><td className="pr-4 font-semibold uppercase text-xs">{t.plan || 'pro'}</td><td className="pr-4 whitespace-nowrap">{durationLabel(t)}</td><td className={`pr-4 whitespace-nowrap ${t.status==='unused'?'text-cyan-300':'text-slate-400'}`}>{labels[t.status] || t.status}</td><td className="pr-4 text-xs text-slate-400 whitespace-nowrap">{dates(t.created_at)}</td><td>{t.status==='unused' && <button disabled={busy} onClick={()=>revoke(t.id)} className="text-rose-300 hover:underline disabled:opacity-50">Cabut</button>}</td></tr>)}
      {!items.length && <tr><td colSpan={6} className="py-6 text-center text-slate-500">Belum ada token. Pilih paket dan durasi untuk membuat token pertama.</td></tr>}
    </tbody></table></div>
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-white/10 pt-4">
      <p className="text-xs text-slate-400">{pagination.total ? `Menampilkan ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, pagination.total)} dari ${pagination.total} token` : 'Belum ada riwayat token'}</p>
      <div className="flex items-center gap-2" role="navigation" aria-label="Halaman riwayat token">
        <Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={()=>setPage(current=>current-1)} aria-label="Halaman sebelumnya" className="px-2 sm:px-3"><ChevronLeft /><span className="sr-only sm:not-sr-only">Sebelumnya</span></Button>
        <span className="min-w-24 text-center text-sm text-slate-300">Halaman {page} dari {pagination.pages}</span>
        <Button type="button" variant="outline" size="sm" disabled={page >= pagination.pages} onClick={()=>setPage(current=>current+1)} aria-label="Halaman berikutnya" className="px-2 sm:px-3"><span className="sr-only sm:not-sr-only">Berikutnya</span><ChevronRight /></Button>
      </div>
    </div>
  </section>;
}
